/**
 * 通用邮件发送抽屉 (GenericEmailSendDrawer) - V2 重构版
 *
 * 设计理念：
 * - 邮件内容 = 模板 + 业务数据
 * - 抽屉层：模板选择 + 数据预览（并行存在）
 * - 编辑器层：独立的 Modal，通过 editorVisible 控制
 *
 * 入口场景：
 * 1. 通用入口（发件箱）：用户手动选择模板和数据
 * 2. 业务页面入口：预设数据 + 自动筛选模板
 *
 * @version 2.0.0
 */

import React, { useState, useCallback, useEffect, useMemo } from "react";
import { Drawer, Button, Space, message, Input, List, Avatar, Tag, Empty, Spin, Popover, Typography, Modal, Divider } from "antd";
import { DatabaseOutlined, MailOutlined, ArrowRightOutlined, SearchOutlined, CheckCircleOutlined, RetweetOutlined } from "@ant-design/icons";
import GeneralEmailEditor from "../EmailEditor/GeneralEmailEditor";
import { emailTemplateApi } from "../../api/emailTemplateApi";
import { sendUniversalEmail } from "../../api/flightOperationEmailApi";
import { getEmailResendDraft } from "../../api/mailboxApi";
import { DataSelectorFactory, inferSelectorFromContentBlocks } from "../common/DataSelectors";
import { EmailType, SendingOptionType, getInitialOptionValues, getAllOptions } from "../../config/emailTypeConfig";
import { getPresetSenderAccounts } from "../../api/mailApi";
import { useAuth } from "../../hooks/useAuth";
import { extractErrorInfo } from "../../utils/errorHandler";

const DATA_TYPE_TO_TEMPLATE_CATEGORY = {
    FLIGHT_OPERATION: "FLIGHT_OPERATION",
    SCHEDULE: "FLIGHT_SCHEDULE",
    GENERAL: "GENERAL",
};

/**
 * 发送前清洗附件载荷：只保留后端 MailJobRequest.attachments 契约字段。
 * 重发复用附件会带 is_reused_from_resend / object_key / available 等 UI 字段；
 * 本地新上传会带 success / download_url。这些多余字段通常可忽略，
 * 但 file_size=0 会被后端 gt=0 校验直接 422，这里主动丢掉无效 file_size。
 */
const sanitizeAttachmentsForSend = (attachments) => {
    if (!Array.isArray(attachments) || attachments.length === 0) {
        return [];
    }

    return attachments
        .map((attachment) => {
            if (!attachment || typeof attachment !== "object") return null;

            const storagePath = attachment.storage_path || attachment.object_key;
            if (!storagePath) return null;

            const cleaned = {
                filename: attachment.filename || attachment.original_filename || "attachment",
                storage_path: storagePath,
                is_inline: Boolean(attachment.is_inline),
            };

            if (attachment.content_type) {
                cleaned.content_type = attachment.content_type;
            }
            if (typeof attachment.file_size === "number" && attachment.file_size > 0) {
                cleaned.file_size = attachment.file_size;
            }
            if (attachment.content_id) {
                cleaned.content_id = attachment.content_id;
            }
            if (typeof attachment.attachment_url === "string" && attachment.attachment_url) {
                cleaned.attachment_url = attachment.attachment_url;
            }
            if (attachment.source_attachment_id != null) {
                cleaned.source_attachment_id = attachment.source_attachment_id;
            }

            return cleaned;
        })
        .filter(Boolean);
};

/**
 * 根据数据样本推断数据类型
 */
const inferDataType = (data) => {
    if (!data || data.length === 0) return null;
    const sample = data[0];

    // 检查是否为执行航班数据
    if (sample.flight_number || sample.flight_id || sample.stc_type) {
        return "FLIGHT_OPERATION";
    }
    // 检查是否为计划数据
    if (sample.schedule_id) {
        return "SCHEDULE";
    }
    return "GENERAL";
};

// ============ 主组件 ============

/**
 * GenericEmailSendDrawer 通用邮件发送抽屉
 *
 * @param {Object} props
 * @param {boolean} props.visible - 是否显示抽屉
 * @param {function} props.onClose - 关闭回调
 * @param {function} props.onSuccess - 发送成功回调
 * @param {Array} props.presetData - 预设的业务数据（从业务页面传入）
 * @param {string} props.dataType - 数据类型（用于推断模板分类，可选）
 * @param {Object} props.templateFilter - 模板筛选条件
 * @param {string} props.templateFilter.category - 模板分类
 * @param {Array<string>} props.templateFilter.templateCodes - 限定的模板代码列表
 * @param {boolean} props.autoOpenEditor - 当只有一个模板且有数据时是否自动打开编辑器（默认 true）
 * @param {object} props.defaultSenderKey - 默认发件人账号key（可选）
 * @param {number|null} props.resendSourceEmailId - 发件箱重发模式：原邮件 ID。
 *   非空时进入"重发模式"——跳过模板选择和数据选择，调用 getEmailResendDraft 拿种子后直接打开编辑器。
 *   该 prop 通常由 OutboxList（T09）/ EmailDetailDrawer（T08）传入；普通发件路径保持为 null。
 *   设计文档：docs/fzx_doc/邮件/发件箱邮件重发/发件箱邮件重发_功能规格说明书.md
 */
const GenericEmailSendDrawer = ({
    visible,
    onClose,
    onSuccess,
    presetData = [],
    dataType = null,
    templateFilter = {},
    autoOpenEditor = true,
    defaultSenderKey = null,
    resendSourceEmailId = null,
}) => {
    // ============ 权限检查 ============
    const { hasPermission, user: currentUser } = useAuth();
    const canSendEmail = hasPermission("mail:send");

    // ============ 重发模式标识（派生） ============
    // resendSourceEmailId 在抽屉整个生命周期内不应变化（OutboxList 通过 key={id} 强制重建抽屉）
    const isResendMode = resendSourceEmailId !== null && resendSourceEmailId !== undefined;

    // ============ 核心状态 ============

    // 模板相关
    const [availableTemplates, setAvailableTemplates] = useState([]);
    // 发件人账号相关
    const [senderAccounts, setSenderAccounts] = useState([]);
    const [selectedSender, setSelectedSender] = useState(null);
    const [senderAccountsLoading, setSenderAccountsLoading] = useState(false);

    // AAE 模板建议弹窗相关
    const [adviceModalVisible, setAdviceModalVisible] = useState(false);
    const [pendingEmailData, setPendingEmailData] = useState(null);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [templateLoading, setTemplateLoading] = useState(false);
    const [templateSearchText, setTemplateSearchText] = useState("");

    // 数据相关
    const [selectedData, setSelectedData] = useState([]);

    // 编辑器状态
    const [editorVisible, setEditorVisible] = useState(false);
    const [isSending, setIsSending] = useState(false);
    const [sendingProgress, setSendingProgress] = useState("");

    // 发送选项状态 - 通用邮件使用GENERAL类型
    const initialSendingOptions = getInitialOptionValues(EmailType.GENERAL);
    const [sendingOptions, setSendingOptions] = useState(initialSendingOptions);

    // 自动打开编辑器标记（避免重复触发）
    const [autoOpenTriggered, setAutoOpenTriggered] = useState(false);

    // ============ 重发模式状态 ============
    // resendDraftSeed: 后端 GET /v1/mail/emails/{id}/resend-draft 返回的种子；
    // 透传给 GeneralEmailEditor 让编辑器据此预填主题、正文、附件、业务上下文（T07 消费）
    const [resendDraftSeed, setResendDraftSeed] = useState(null);
    const [resendDraftLoading, setResendDraftLoading] = useState(false);

    // ============ 状态管理优化 ============

    // 使用 Ref 追踪初始化状态，避免多次触发
    const initializedRef = React.useRef(false);

    // ============ 派生状态 ============

    // 过滤后的模板列表（支持搜索）
    const filteredTemplates = useMemo(() => {
        if (!templateSearchText.trim()) return availableTemplates;
        const searchLower = templateSearchText.toLowerCase();
        return availableTemplates.filter((t) => t.name?.toLowerCase().includes(searchLower) || t.template_code?.toLowerCase().includes(searchLower) || t.description?.toLowerCase().includes(searchLower));
    }, [availableTemplates, templateSearchText]);

    // 是否可以打开编辑器
    const canOpenEditor = useMemo(() => {
        if (!selectedTemplate) return false;
        // 如果没有预设数据传入（通用入口），不强制要求选择数据
        if (!presetData || presetData.length === 0) {
            return true;
        }
        // 有预设数据时，需要选择了数据才能打开
        return selectedData.length > 0;
    }, [selectedTemplate, selectedData, presetData]);

    // ============ 初始化与数据加载 ============

    // 统一初始化逻辑：当抽屉打开时执行
    useEffect(() => {
        if (!visible) {
            // 抽屉关闭时，重置初始化标记和重发种子（避免下次打开复用旧种子）
            initializedRef.current = false;
            setResendDraftSeed(null);
            return;
        }

        // 如果已经初始化过，且不需要强制重置，则跳过
        if (initializedRef.current) return;

        console.log("[GenericEmailSendDrawer] 初始化...", { isResendMode, resendSourceEmailId });
        initializedRef.current = true;

        // ====== 重发模式分支（发件箱邮件重发 T06） ======
        // 整体策略：
        //   1) 调 getEmailResendDraft 拿种子（含 subject / body_html / template_code / template_available / attachments / content_block_context）
        //   2) 若种子带 template_code 且 template_available=true → 调 emailTemplateApi.getTemplateByCodeWithRecipients 加载真模板对象（编辑器需要 resolved_to / content_blocks 等结构）
        //   3) 若种子 template_code 为空（88% 计划邮件 / 自动转发邮件无模板）→ 构造 ad-hoc 模板对象，仅含编辑器渲染所需的最小字段
        //   4) 跳过模板列表 + 数据选择 UI（drawer body 渲染为占位），直接 setEditorVisible(true)
        if (isResendMode) {
            setEditorVisible(false);
            setAutoOpenTriggered(false);
            setTemplateSearchText("");
            setAvailableTemplates([]);
            setSelectedData([]);

            const loadResendDraft = async () => {
                setResendDraftLoading(true);
                setTemplateLoading(true);
                try {
                    const draftResp = await getEmailResendDraft(resendSourceEmailId);
                    const draft = draftResp.data || draftResp;
                    if (!draft) {
                        message.error("加载重发草稿失败：服务端返回为空");
                        return;
                    }
                    setResendDraftSeed(draft);

                    // 解析模板：种子带可用 template_code 时调真模板，否则构造 ad-hoc 模板
                    let templateForEditor = null;
                    if (draft.template_code && draft.template_available) {
                        try {
                            const detailResp = await emailTemplateApi.getTemplateByCodeWithRecipients(draft.template_code);
                            templateForEditor = detailResp.data || null;
                        } catch (err) {
                            // 模板加载失败（极端情况：模板在我们 GET resend-draft 与 GET template 之间被下线）
                            // 退化为 ad-hoc 模板，编辑器仍能用种子的 body_html 正常工作
                            console.warn("[GenericEmailSendDrawer] 重发模式加载真模板失败，降级为 ad-hoc 模板", err);
                            templateForEditor = null;
                        }
                    }

                    if (!templateForEditor) {
                        // ad-hoc "伪模板"：只填够 drawer + editor 渲染需要的字段
                        // 注意 template_code 设为 null，避免后端在 _create_template_based_category_mapping 里去查不存在的模板
                        templateForEditor = {
                            template_code: draft.template_code || null,
                            name: draft.source_subject ? `重发: ${draft.source_subject}` : "重发邮件",
                            description: `基于邮件 #${draft.source_email_id} 重发`,
                            category: "GENERAL",
                            subject_template: draft.subject || "",
                            body_html: draft.body_html || "",
                            body_text: draft.body_text || "",
                            // 重发场景下编辑器收件人默认为空（FR-003），所以 resolved_to/cc 给空数组
                            resolved_to: [],
                            resolved_cc: [],
                            default_to: { addresses: [], groups: [] },
                            default_cc: { addresses: [], groups: [] },
                            content_blocks: null,
                        };
                    }
                    setSelectedTemplate(templateForEditor);

                    // 直接打开编辑器
                    setEditorVisible(true);
                    setAutoOpenTriggered(true);
                } catch (error) {
                    console.error("[GenericEmailSendDrawer] 加载重发草稿失败:", error);
                    const detail = error?.response?.data?.detail || error?.message || "未知错误";
                    message.error(`加载重发草稿失败: ${detail}`);
                    // 加载失败时不强制关闭抽屉，让用户能看到错误并选择"取消"
                } finally {
                    setResendDraftLoading(false);
                    setTemplateLoading(false);
                }
            };

            loadResendDraft();
            return;
        }

        // ====== 普通发件模式分支（原有逻辑） ======

        // 1. 初始化数据状态
        // 使用传入的 presetData 初始化 selectedData
        // 注意：这里我们创建副本，避免直接引用 props
        const initialData = presetData ? [...presetData] : [];
        setSelectedData(initialData);

        // 重置 UI 状态
        setTemplateSearchText("");
        setEditorVisible(false);
        setAutoOpenTriggered(false);
        setSelectedTemplate(null);

        // 2. 计算初始模板分类
        // 逻辑：优先使用 templateFilter.category -> 其次根据 presetData 推断 -> 默认 GENERAL
        let initialCategory = "GENERAL";
        if (templateFilter?.category) {
            initialCategory = templateFilter.category;
        } else if (presetData?.length > 0) {
            const inferredType = inferDataType(presetData);
            if (inferredType && DATA_TYPE_TO_TEMPLATE_CATEGORY[inferredType]) {
                initialCategory = DATA_TYPE_TO_TEMPLATE_CATEGORY[inferredType];
            }
        }

        // 3. 加载模板
        const loadTemplates = async () => {
            setTemplateLoading(true);
            try {
                const params = {
                    status: "ACTIVE",
                    page_size: 50,
                };

                // 应用分类筛选（排除 GENERAL 以获取所有模板，或者根据需求限制）
                // 之前的逻辑是：如果是 GENERAL 则不传 category 从而获取所有?
                // 或者：如果是 GENERAL 则获取所有，如果是特定分类则筛选
                if (initialCategory && initialCategory !== "GENERAL") {
                    params.category = initialCategory;
                }

                // 应用模板代码筛选
                if (templateFilter?.templateCodes?.length > 0) {
                    params.template_codes = templateFilter.templateCodes.join(",");
                }

                console.log("[GenericEmailSendDrawer] 加载模板，参数:", params);
                const response = await emailTemplateApi.getTemplates(params);
                const templates = response.data?.items || [];
                setAvailableTemplates(templates);

                // 4. 自动选择逻辑
                if (templates.length > 0) {
                    // 默认选中第一个
                    const firstTemplate = templates[0];
                    console.log("[GenericEmailSendDrawer] 默认选中模板:", firstTemplate.template_code);

                    // 获取详情
                    try {
                        const detailResponse = await emailTemplateApi.getTemplateByCodeWithRecipients(firstTemplate.template_code);
                        const fullTemplate = detailResponse.data;
                        setSelectedTemplate(fullTemplate);

                        // 5. 自动打开编辑器逻辑 (移到这里，确保时序正确)
                        // 条件：开启自动打开 + 只有一个模板 + 有数据
                        if (autoOpenEditor && templates.length === 1 && initialData.length > 0) {
                            console.log("[GenericEmailSendDrawer] 触发自动打开编辑器");
                            setEditorVisible(true);
                            setAutoOpenTriggered(true);
                        }
                    } catch (err) {
                        console.error("获取模板详情失败", err);
                    }
                }
            } catch (error) {
                console.error("[GenericEmailSendDrawer] 加载模板列失败:", error);
                message.error("加载邮件模板失败");
            } finally {
                setTemplateLoading(false);
            }
        };

        loadTemplates();
    }, [visible, isResendMode, resendSourceEmailId]); // 仅依赖 visible / resend 相关，其他依赖通过 ref 或闭包获取最新值（初始化时刻的值）

    // 加载预设发件账号
    useEffect(() => {
        if (visible) {
            setSenderAccountsLoading(true);
            getPresetSenderAccounts()
                .then((res) => {
                    if (res.success) {
                        const accounts = res.data || [];
                        setSenderAccounts(accounts);

                        // 根据defaultSenderKey设置默认发件人
                        if (defaultSenderKey) {
                            const defaultAccount = accounts.find((acc) => acc.key === defaultSenderKey);
                            setSelectedSender(defaultAccount || null);
                        }
                    }
                })
                .catch((err) => {
                    console.error("加载预设发件账号失败:", err);
                    message.error("加载发件账号失败，请稍后重试");
                })
                .finally(() => {
                    setSenderAccountsLoading(false);
                });
        }
    }, [visible, defaultSenderKey]);

    // 移除之前的多个 useEffect，避免逻辑冲突和循环依赖

    // ============ 数据搜索逻辑 ============

    const handleDataSearch = useCallback(async () => {
        // TODO: 未来可扩展支持从抽屉内搜索航班数据
        // 目前主要使用场景是业务页面传入预设数据
        message.info("数据搜索功能开发中，请从业务页面选择航班后再打开邮件发送");
    }, []);

    // ============ 模板选择逻辑 ============

    const handleTemplateSelect = useCallback(
        async (template) => {
            if (selectedTemplate?.template_code === template.template_code) return;

            setTemplateLoading(true);
            try {
                const detailResponse = await emailTemplateApi.getTemplateByCodeWithRecipients(template.template_code);
                setSelectedTemplate(detailResponse.data);
                console.log("[GenericEmailSendDrawer] 选择模板:", template.template_code);
            } catch (error) {
                console.error("[GenericEmailSendDrawer] 加载模板详情失败:", error);
                message.error("加载模板详情失败");
            } finally {
                setTemplateLoading(false);
            }
        },
        [selectedTemplate],
    );

    // ============ 数据选择逻辑 ============

    const handleDataRowSelect = useCallback((record, selected) => {
        if (selected) {
            setSelectedData((prev) => {
                if (prev.some((item) => item.id === record.id)) return prev;
                return [...prev, record];
            });
        } else {
            setSelectedData((prev) => prev.filter((item) => item.id !== record.id));
        }
    }, []);

    const handleDataRemove = useCallback((recordId) => {
        setSelectedData((prev) => prev.filter((item) => item.id !== recordId));
    }, []);

    // ============ 编辑器相关 ============

    // 生成邮件上下文数据
    // 生成邮件上下文数据
    const generateEmailContextData = useCallback(() => {
        if (!selectedData || selectedData.length === 0) return {};

        // 内部计算 dataType
        const currentDataType = dataType || inferDataType(selectedData);

        const contentBlocks = [];

        if (currentDataType === "FLIGHT_OPERATION") {
            contentBlocks.push({
                id: "operation_flight_list",
                type: "operationFlightList",
                title: "航班列表",
                data: selectedData,
                business_source: {
                    entity_type: "FlightOperation",
                    entity_ids: selectedData.map((f) => f.id),
                },
            });
        } else if (currentDataType === "SCHEDULE") {
            contentBlocks.push({
                id: "schedule_flight_list",
                type: "scheduleFlightList",
                title: "计划航班列表",
                data: selectedData,
                business_source: {
                    entity_type: "Schedule",
                    entity_ids: selectedData.map((s) => s.id),
                },
            });
        }

        return {
            contentBlocks,
            flightOperations: currentDataType === "FLIGHT_OPERATION" ? selectedData : undefined,
            schedules: currentDataType === "SCHEDULE" ? selectedData : undefined,
            dataPayload: selectedData,
            rawData: {
                flights: selectedData,
            },
        };
    }, [selectedData, dataType]);

    // 获取模板收件人（使用后端解析后的邮箱列表）
    const templateRecipients = useMemo(() => {
        if (!selectedTemplate) return { default_to: [], default_cc: [] };
        // 使用 resolved_to/resolved_cc（后端已解析分组为具体邮箱）
        // 这样前端不需要再处理分组解析
        return {
            default_to: selectedTemplate.resolved_to || [],
            default_cc: selectedTemplate.resolved_cc || [],
        };
    }, [selectedTemplate]);

    // 打开编辑器
    const handleOpenEditor = useCallback(() => {
        if (!canOpenEditor) {
            if (!selectedTemplate) {
                message.warning("请先选择一个邮件模板");
            } else if (selectedData.length === 0) {
                message.warning("请先选择业务数据");
            }
            return;
        }
        setEditorVisible(true);
    }, [canOpenEditor, selectedTemplate, selectedData]);

    // 关闭编辑器（回到抽屉）
    const handleEditorCancel = useCallback(() => {
        setEditorVisible(false);
    }, []);

    // 处理邮件发送（核心执行逻辑）
    const executeEmailSend = async (emailData) => {
        // 权限检查：确保用户有发送邮件的权限
        if (!canSendEmail) {
            message.error("您没有发送邮件的权限，请联系管理员");
            return;
        }

        try {
            setIsSending(true);
            setSendingProgress("正在发送邮件...");

            // 构建收件人列表
            const recipients = [];
            emailData.to?.forEach((email) => {
                if (email?.trim()) {
                    recipients.push({ email: email.trim(), type: "TO" });
                }
            });
            emailData.cc?.forEach((email) => {
                if (email?.trim()) {
                    recipients.push({ email: email.trim(), type: "CC" });
                }
            });

            if (recipients.length === 0) {
                message.error("请至少添加一个收件人");
                return;
            }

            // 检查重复邮箱地址（同一邮箱不能同时出现在 TO 和 CC 中）
            const emailAddresses = recipients.filter((r) => r.email.includes("@")).map((r) => r.email.toLowerCase());
            const uniqueEmails = new Set(emailAddresses);
            if (emailAddresses.length !== uniqueEmails.size) {
                // 找出重复的邮箱
                const seen = new Set();
                const duplicates = [];
                emailAddresses.forEach((email) => {
                    if (seen.has(email) && !duplicates.includes(email)) {
                        duplicates.push(email);
                    }
                    seen.add(email);
                });
                message.error(`以下邮箱地址重复出现在收件人或抄送列表中：${duplicates.join(", ")}。请移除重复的邮箱地址。`);
                return;
            }

            // ====== 业务关联 + content_block_context 构造（区分普通发件 vs 重发） ======
            //
            // 重发模式（FR-005 / FR-009 / FR-011）：
            //   - business_link 字段不传，由后端 T02 universal_mail_service 从原邮件 raw_email_business_link 自动继承
            //     （避免前端在重发时硬拼一个跟原邮件不一致的 business_link）
            //   - content_block_context.resend_source 必须填，承载链式重发的"直接上级"指针（FR-009 不递归）
            //   - content_block_context 其余字段（template_code / business_context_snapshot）按原邮件种子原样回填，
            //     编辑器没有引入新数据选择就不会破坏原业务上下文
            //
            // 普通发件模式（原有逻辑）：
            //   - 按 selectedData 构造 business_link + business_context_snapshot
            let businessLink = null;
            let contentBlockContext;

            if (isResendMode && resendDraftSeed) {
                // 重发：不传 business_link，让后端继承
                businessLink = null;
                contentBlockContext = {
                    // 原邮件可能没有 template_code（88% 计划邮件 / 自动转发邮件），保持为 null/undefined 让后端跳过模板分类映射
                    ...(resendDraftSeed.content_block_context || {}),
                    // FR-009：resend_source.email_id 指向直接上级，不递归回溯到最早那封
                    resend_source: {
                        email_id: resendDraftSeed.source_email_id,
                        sent_at: resendDraftSeed.source_sent_at || null,
                    },
                };
            } else {
                const currentDataType = dataType || inferDataType(selectedData);
                const entityIds = selectedData.map((item) => item.id);
                businessLink = {
                    record_id: entityIds[0] || 0,
                    record_type: currentDataType || "GENERAL",
                    link_type: `${currentDataType || "GENERAL"}_APPLICATION`,
                    details: {
                        entity_ids: entityIds,
                        data_type: currentDataType,
                        batch_size: entityIds.length,
                    },
                };
                contentBlockContext = {
                    template_code: selectedTemplate?.template_code || "UNKNOWN",
                    instances: [],
                    business_context_snapshot: generateEmailContextData(),
                };
            }

            // 调用统一邮件API
            // 注意：使用组件状态 sendingOptions 作为数据来源，而非 emailData.splitSend（不存在）
            const requestData = {
                // 重发模式下 business_link 为 null，让后端自动继承（FR-005）
                ...(businessLink ? { business_link: businessLink } : {}),
                recipients,
                subject: emailData.subject || "",
                body_html: emailData.contentHtml || "",
                attachments: sanitizeAttachmentsForSend(emailData.attachments),
                // FR-011：重发新邮件 sender_user_id 必须等于"当前操作用户"，不等于原邮件 sender_user_id
                // 历史遗留硬编码（sender_user_id: 1）只在 currentUser 不可用时兜底
                sender_user_id: currentUser?.id || 1,
                // 使用组件状态中的 sendingOptions（通过 GeneralEmailEditor 的 onSendingOptionsChange 更新）
                is_bulk_split: sendingOptions[SendingOptionType.BCC_SENDING] || false,
                request_read_receipt: sendingOptions[SendingOptionType.READ_RECEIPT] || false,
                preset_sender_key: emailData.selectedSender?.key || null, // 传递预设发件账号标识
                content_block_context: contentBlockContext,
            };

            const response = await sendUniversalEmail(requestData);

            if (response.success) {
                const useSplitSending = sendingOptions[SendingOptionType.BCC_SENDING];

                if (useSplitSending) {
                    // 异步发送模式 - 显示详细提示并引导查看发件箱
                    message.success({
                        content: (
                            <div>
                                <div style={{ fontWeight: "bold", marginBottom: 4 }}>邮件发送任务已启动</div>
                                <div style={{ fontSize: "12px", color: "#666" }}>{response.message || "邮件正在后台发送中"}</div>
                                <div style={{ fontSize: "12px", color: "#1890ff", marginTop: 4 }}>由于开启了"群发单显"，系统将在后台逐一发送邮件</div>
                                <div style={{ fontSize: "12px", color: "#52c41a", marginTop: 4 }}>可前往「发件箱」查看发送进度和结果</div>
                            </div>
                        ),
                        duration: 8,
                    });
                } else {
                    // 同步发送模式 - 显示简洁的成功提示
                    message.success({
                        content: (
                            <div>
                                <div style={{ fontWeight: "bold", marginBottom: 4 }}>邮件发送成功</div>
                                <div style={{ fontSize: "12px", color: "#666" }}>{response.message || "邮件已成功发送"}</div>
                            </div>
                        ),
                        duration: 4,
                    });
                }

                setEditorVisible(false);
                onClose();
                onSuccess?.();
            } else {
                message.error(response.message || "邮件发送失败");
            }
        } catch (error) {
            console.error("发送邮件失败:", error);
            const errorInfo = extractErrorInfo(error);
            message.error({
                content: (
                    <div>
                        <div style={{ fontWeight: "bold", marginBottom: 4 }}>邮件发送失败</div>
                        <div style={{ fontSize: "12px", color: "#666", whiteSpace: "pre-wrap" }}>{errorInfo.message || "发送邮件时发生错误"}</div>
                    </div>
                ),
                duration: 8,
            });
        } finally {
            setIsSending(false);
            setSendingProgress("");
        }
    };

    // 新的入口函数：增加 AAE 模板的 Split Send 建议逻辑
    const handleEmailSubmit = async (emailData) => {
        // 检查是否为 AAE 模板 (硬编码 code) 且未开启 Split Send
        if (selectedTemplate?.template_code === "OPERATION_FLIGHT_AAE_SCHEDULE" && !emailData.splitSend) {
            setPendingEmailData(emailData);
            setAdviceModalVisible(true);
            return;
        }
        await executeEmailSend(emailData);
    };

    const handleAdviceConfirm = () => {
        if (pendingEmailData) {
            const newData = { ...pendingEmailData, splitSend: true };
            setAdviceModalVisible(false);
            setPendingEmailData(null);
            executeEmailSend(newData);
        }
    };

    const handleAdviceIgnore = () => {
        if (pendingEmailData) {
            setAdviceModalVisible(false);
            setPendingEmailData(null);
            executeEmailSend(pendingEmailData);
        }
    };

    const handleAdviceCancel = () => {
        setAdviceModalVisible(false);
        setPendingEmailData(null);
    };

    // ============ 关闭抽屉 ============

    const handleClose = useCallback(() => {
        // 如果编辑器正在发送，不允许关闭
        if (isSending) return;

        // 如果编辑器打开，先关闭编辑器
        if (editorVisible) {
            setEditorVisible(false);
            return;
        }

        onClose();
    }, [isSending, editorVisible, onClose]);

    // ============ 渲染：模板列表 ============

    // ============ 渲染：模板列表项 ============

    const TemplateListItem = React.memo(({ item, isSelected, onSelect }) => {
        // 模板预览内容
        const previewContent = (
            <div style={{ maxWidth: 350 }}>
                <div style={{ marginBottom: 12 }}>
                    <div
                        style={{
                            fontWeight: 600,
                            marginBottom: 4,
                            color: "#1d39c4",
                            borderBottom: "1px solid #f0f0f0",
                            paddingBottom: 4,
                        }}
                    >
                        {item.name}
                    </div>
                    <div style={{ fontSize: 12, color: "#8c8c8c", marginBottom: 8 }}>{item.description}</div>
                </div>

                {(item.default_to?.addresses?.length > 0 || item.default_to?.groups?.length > 0) && (
                    <div style={{ marginBottom: 8 }}>
                        <div style={{ fontWeight: "bold", fontSize: 12 }}>默认收件：</div>
                        <div style={{ fontSize: 12, color: "#595959" }}>
                            {item.default_to.addresses?.join(", ")}
                            {item.default_to.addresses?.length > 0 && item.default_to.groups?.length > 0 && ", "}
                            {item.default_to.groups?.map((g) => `[${g}]`).join(", ")}
                        </div>
                    </div>
                )}

                {(item.default_cc?.addresses?.length > 0 || item.default_cc?.groups?.length > 0) && (
                    <div style={{ marginBottom: 8 }}>
                        <div style={{ fontWeight: "bold", fontSize: 12 }}>默认抄送：</div>
                        <div style={{ fontSize: 12, color: "#595959" }}>
                            {item.default_cc.addresses?.join(", ")}
                            {item.default_cc.addresses?.length > 0 && item.default_cc.groups?.length > 0 && ", "}
                            {item.default_cc.groups?.map((g) => `[${g}]`).join(", ")}
                        </div>
                    </div>
                )}

                <div>
                    <div style={{ fontWeight: "bold", fontSize: 12, marginBottom: 4 }}>正文预览：</div>
                    <div
                        style={{
                            fontSize: 12,
                            color: "#595959",
                            background: "#f5f5f5",
                            padding: "8px",
                            borderRadius: "4px",
                            maxHeight: "150px",
                            overflowY: "auto",
                        }}
                    >
                        {item.body_text ? (
                            <Typography.Paragraph ellipsis={{ rows: 5, expandable: false }} style={{ marginBottom: 0, fontSize: 12 }}>
                                {item.body_text}
                            </Typography.Paragraph>
                        ) : (
                            <span style={{ color: "#bfbfbf" }}>无文本预览</span>
                        )}
                    </div>
                </div>
            </div>
        );

        return (
            <Popover content={previewContent} placement="rightTop" mouseEnterDelay={1.2} overlayInnerStyle={{ padding: 16 }}>
                <div onClick={() => onSelect(item)}>
                    <List.Item
                        style={{
                            padding: "12px 16px",
                            cursor: "pointer",
                            background: isSelected ? "#e6f7ff" : "transparent",
                            borderLeft: isSelected ? "3px solid #1890ff" : "3px solid transparent",
                            transition: "all 0.2s",
                        }}
                    >
                        <List.Item.Meta
                            avatar={<Avatar shape="square" size="small" style={{ backgroundColor: isSelected ? "#1890ff" : "#d9d9d9" }} icon={<MailOutlined />} />}
                            title={
                                <Space>
                                    <span
                                        style={{
                                            fontWeight: isSelected ? 600 : 400,
                                            color: isSelected ? "#1890ff" : "#333",
                                            fontSize: 13,
                                        }}
                                    >
                                        {item.name}
                                    </span>
                                    {isSelected && <CheckCircleOutlined style={{ color: "#52c41a" }} />}
                                </Space>
                            }
                            description={
                                <div>
                                    <div
                                        style={{
                                            fontSize: 11,
                                            color: "#999",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap",
                                            marginBottom: 2,
                                        }}
                                    >
                                        {item.description || "暂无描述"}
                                    </div>
                                    <Tag
                                        color="blue"
                                        style={{
                                            fontSize: 10,
                                            lineHeight: "16px",
                                            padding: "0 4px",
                                            marginRight: 0,
                                            transform: "scale(0.9)",
                                            transformOrigin: "left center",
                                        }}
                                    >
                                        {item.category || "General"}
                                    </Tag>
                                </div>
                            }
                        />
                    </List.Item>
                </div>
            </Popover>
        );
    });

    // ============ 渲染：模板列表 ============

    const renderTemplateList = () => (
        <div
            style={{
                width: "280px",
                background: "#fff",
                borderRight: "1px solid #f0f0f0",
                display: "flex",
                flexDirection: "column",
                height: "100%",
            }}
        >
            <div style={{ padding: "16px", borderBottom: "1px solid #f0f0f0" }}>
                <div style={{ fontSize: "14px", fontWeight: "600", marginBottom: "12px" }}>
                    选择模板
                    {availableTemplates.length > 0 && (
                        <Tag color="blue" style={{ marginLeft: 8 }}>
                            {availableTemplates.length}
                        </Tag>
                    )}
                </div>
                <Input placeholder="搜索模板..." size="small" prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />} value={templateSearchText} onChange={(e) => setTemplateSearchText(e.target.value)} allowClear />
            </div>
            <div style={{ flex: 1, overflowY: "auto" }}>
                {templateLoading ? (
                    <div style={{ padding: "40px", textAlign: "center" }}>
                        <Spin tip="加载模板中..." />
                    </div>
                ) : filteredTemplates.length === 0 ? (
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无可用模板" />
                ) : (
                    <List itemLayout="horizontal" dataSource={filteredTemplates} renderItem={(item) => <TemplateListItem key={item.template_code} item={item} isSelected={selectedTemplate?.template_code === item.template_code} onSelect={handleTemplateSelect} />} />
                )}
            </div>
        </div>
    );

    // ============ 渲染：数据选择区 ============

    const renderDataSection = () => {
        // 获取当前模板的内容块类型
        const contentBlocks = selectedTemplate?.content_blocks;

        // 判断是否需要数据选择器
        const selectorType = contentBlocks ? inferSelectorFromContentBlocks(contentBlocks) : null;

        // 使用 DataSelectorFactory 渲染数据选择器
        return <DataSelectorFactory type={selectorType} contentBlocks={contentBlocks} value={selectedData} onChange={setSelectedData} presetData={presetData} mode="multiple" disabled={false} allowSkip={true} onSkip={handleOpenEditor} style={{ flex: 1, height: "100%", overflow: "hidden" }} />;
    };

    // ============ 渲染：编辑器 Modal ============

    const renderEditor = () => {
        if (!editorVisible || !selectedTemplate) return null;

        // 重发模式：subject / body / businessContext 都来自种子（FR-001 不修改原邮件、原样预填）
        // 编辑器内部按 resendDraftSeed prop 检测重发模式、加顶部提示条、收件人按钮等（T07 消费）
        const editorTitle = isResendMode
            ? `基于邮件 #${resendDraftSeed?.source_email_id ?? ""} 重发`
            : `${selectedTemplate.name || "邮件"}编辑`;

        return (
            <GeneralEmailEditor
                visible={editorVisible}
                emailType={EmailType.GENERAL}
                sendingOptions={sendingOptions}
                onSendingOptionsChange={setSendingOptions}
                senderOptions={senderAccounts}
                defaultSender={selectedSender}
                senderEditable={true}
                onSubmit={handleEmailSubmit}
                onCancel={handleEditorCancel}
                title={editorTitle}
                subjectTemplate={selectedTemplate.subject_template || ""}
                templateContent={selectedTemplate.body_html || ""}
                templateRecipients={templateRecipients}
                businessContext={isResendMode ? (resendDraftSeed?.content_block_context?.business_context_snapshot || {}) : generateEmailContextData()}
                loading={isSending}
                loadingText={sendingProgress}
                cancelDisabled={isSending}
                // 发件箱邮件重发 T07 消费：检测到非空时进入重发模式 UI（顶部 Alert、沿用按钮、失效附件标红等）
                resendDraftSeed={isResendMode ? resendDraftSeed : null}
            />
        );
    };

    // ============ 主渲染 ============

    // 重发模式占位（drawer body 在编辑器 Modal 打开之前显示一个加载/错误状态，避免空白）
    const renderResendPlaceholder = () => (
        <div
            style={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                padding: 48,
                color: "#8c8c8c",
            }}
        >
            {resendDraftLoading ? (
                <>
                    <Spin size="large" />
                    <Typography.Text style={{ marginTop: 16 }} type="secondary">
                        正在加载重发草稿...
                    </Typography.Text>
                </>
            ) : (
                <>
                    <RetweetOutlined style={{ fontSize: 48, color: "#bfbfbf", marginBottom: 16 }} />
                    <Typography.Text type="secondary">
                        {resendDraftSeed ? "重发编辑器已打开，关闭后将返回此页" : "重发草稿加载失败，请关闭重试"}
                    </Typography.Text>
                </>
            )}
        </div>
    );

    return (
        <>
            <Drawer
                title={
                    <Space>
                        {isResendMode ? <RetweetOutlined /> : <MailOutlined />}
                        <span>{isResendMode ? "重发邮件" : "写信"}</span>
                        {isResendMode && resendSourceEmailId != null && (
                            <>
                                <Divider type="vertical" />
                                <Tag color="orange" icon={<RetweetOutlined />}>
                                    基于邮件 #{resendSourceEmailId} 重发
                                </Tag>
                            </>
                        )}
                        {!isResendMode && selectedTemplate && (
                            <>
                                <Divider type="vertical" />
                                <Tag color="blue">{selectedTemplate.name}</Tag>
                            </>
                        )}
                    </Space>
                }
                open={visible}
                onClose={handleClose}
                width="80%"
                destroyOnClose
                maskClosable={!isSending}
                closable={!isSending}
                extra={
                    isResendMode ? (
                        // 重发模式：直接展示"取消"，不展示"打开编辑器"（编辑器自动打开）
                        <Space>
                            <Button onClick={handleClose}>取消</Button>
                        </Space>
                    ) : (
                        <Space>
                            <Button onClick={handleClose}>取消</Button>
                            <Button type="primary" icon={<ArrowRightOutlined />} onClick={handleOpenEditor} disabled={!canOpenEditor}>
                                打开编辑器
                            </Button>
                        </Space>
                    )
                }
                styles={{
                    body: { padding: 0, display: "flex", height: "calc(100vh - 55px)", position: "relative" },
                }}
            >
                {isResendMode ? (
                    /* 重发模式：跳过模板列表和数据选择，渲染占位（编辑器 Modal 会盖在上面） */
                    renderResendPlaceholder()
                ) : (
                    <>
                        {/* 左侧：模板列表 */}
                        {renderTemplateList()}

                        {/* 右侧：数据选择区 */}
                        {renderDataSection()}
                    </>
                )}

                {/* 发送中的遮罩层 */}
                {isSending && (
                    <div
                        style={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            background: "rgba(255, 255, 255, 0.7)",
                            zIndex: 1000,
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                            alignItems: "center",
                        }}
                    >
                        <Spin size="large" />
                        <Typography.Text style={{ marginTop: 16 }} type="secondary">
                            {sendingProgress || "正在发送..."}
                        </Typography.Text>
                    </div>
                )}
            </Drawer>

            {/* 编辑器 Modal（独立层级） */}
            {renderEditor()}

            <Modal
                title='建议开启"单独发送"'
                open={adviceModalVisible}
                onCancel={handleAdviceCancel}
                footer={[
                    <Button key="cancel" onClick={handleAdviceCancel}>
                        取消
                    </Button>,
                    <Button key="ignore" onClick={handleAdviceIgnore}>
                        忽略建议，直接发送
                    </Button>,
                    <Button key="submit" type="primary" onClick={handleAdviceConfirm}>
                        开启并发送
                    </Button>,
                ]}
            >
                <p>检测到您使用的是 AAE 模板。</p>
                <p>是否需要单独发送（为每个收件人单独发送一封邮件）？</p>
            </Modal>
        </>
    );
};

export default GenericEmailSendDrawer;
